import { useEffect, useRef } from "react"
import { selectAuthToken } from "../../../login/loginSelectors"
import { useAppSelector } from "../../../store/storeHooks"

type SupportUiMessage =
    | { type: "support-ui:ready" }
    | { type: "support-ui:token"; token: string }

export function SupportPaymentsPage() {
    const token = useAppSelector(selectAuthToken) ?? ""
    const ref = useRef<HTMLIFrameElement>(null)
    const tokenRef = useRef(token)
    tokenRef.current = token

    useEffect(() => {
        const onMessage = (e: MessageEvent<unknown>): void => {
            if (e.origin !== window.location.origin) return

            // Narrows e.source to Window, and confirms it's our iframe.
            const frame = ref.current?.contentWindow
            if (!frame || e.source !== frame) return

            const msg = e.data as Partial<SupportUiMessage> | null
            if (msg?.type !== "support-ui:ready") return

            const reply: SupportUiMessage = {
                type: "support-ui:token",
                token: tokenRef.current,
            }
            frame.postMessage(reply, window.location.origin)
        }

        window.addEventListener("message", onMessage)
        return () => window.removeEventListener("message", onMessage)
    }, [])

    useEffect(() => {
        const reply: SupportUiMessage = { type: "support-ui:token", token }
        ref.current?.contentWindow?.postMessage(reply, window.location.origin)
    }, [token])

    // The support UI scrolls internally (see payment-core's index.css) rather than
    // being resized to fit its content - it used to post its content height back here
    // via a "support-ui:height" message so this iframe could grow/shrink to match, but
    // that made position:fixed content inside it (e.g. a payment detail drawer) pin to
    // the wrong place, since the iframe's own "viewport" was its entire, ever-growing
    // document rather than whatever was actually on screen. calc(100vh - 5rem) mirrors
    // the same top-bar allowance RoadmapContent.tsx uses elsewhere in this app.
    return (
        <iframe
            ref={ref}
            src="/api/public/payments/ui/"
            style={{ width: "100%", height: "calc(100vh - 5rem)", border: 0 }}
        />
    )
}
